import { useCallback, useEffect, useState } from "react";
import { act as ccAct, addWork as ccAddWork, fetchBoard } from "@/lib/ccApi";

// Board hydration: live from the API when reachable, baked committed data otherwise.
// Actions apply the server's response straight into the local rows (no refetch needed)
// and every failure surfaces as plain-English notice text. Boards never go dark.
export function useBoard(boardName, { pollMs = 0 } = {}) {
  const [items, setItems] = useState(null); // null = not loaded → pages fall back to baked data
  const [meta, setMeta] = useState(null); // stamp block from the live board (ingest freshness, totals)
  const [status, setStatus] = useState("loading"); // loading | live | needs-signin | down
  const [pending, setPending] = useState(null); // target slug currently acting
  const [notice, setNotice] = useState(null); // { tone: "green" | "amber", text }

  const load = useCallback(async () => {
    try {
      const body = await fetchBoard(boardName);
      setItems(body.items);
      setMeta(body.meta || null);
      setStatus("live");
      return true;
    } catch (e) {
      setStatus(e.kind === "auth" ? "needs-signin" : "down");
      return false;
    }
  }, [boardName]);

  useEffect(() => {
    setItems(null);
    setStatus("loading");
    load();
  }, [load]);

  // Keep the board current: refresh when Jeff returns to the tab (cheap, no sockets).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [load]);

  const act = useCallback(
    async (verb, target, note) => {
      setPending(target);
      try {
        const res = await ccAct({ verb, board: boardName, target, note });
        setItems((prev) =>
          (prev || []).map((r) => {
            if (r.slug !== target) return r;
            const mergedPayload = { ...r.payload, ...(res.payload || {}) };
            const overlayNow = { ...(r.payload.jeff || {}), ...(res.overlay || {}) };
            if (Object.keys(overlayNow).length) mergedPayload.jeff = overlayNow;
            return { ...r, state: res.state, payload: mergedPayload };
          }),
        );
        setNotice({ tone: "green", text: "" }); // cleared; pages add their own confirmation
        return res;
      } catch (e) {
        if (e.kind === "auth") setStatus("needs-signin");
        else setNotice({ tone: "amber", text: e.message });
        return null;
      } finally {
        setPending(null);
      }
    },
    [boardName],
  );

  // Editing verbs (same surface, one path): value carries the new title or text,
  // move carries an up/down nudge. Responses ride back into the row like act().
  const edit = useCallback(
    async (verb, target, fields = {}) => {
      setPending(target);
      try {
        const res = await ccAct({ verb, board: boardName, target, ...fields });
        setItems((prev) => {
          if (!prev) return prev;
          if (res.deleted) return prev.filter((r) => r.slug !== target);
          return prev.map((r) => {
            if (r.slug !== target) return r;
            const mergedPayload = { ...r.payload, ...(res.payload || {}) };
            const overlayNow = { ...(r.payload.jeff || {}), ...(res.overlay || {}) };
            if (Object.keys(overlayNow).length) mergedPayload.jeff = overlayNow;
            return {
              ...r,
              state: res.state ?? r.state,
              ...(res.sort !== undefined ? { sort: res.sort } : {}),
              payload: mergedPayload,
            };
          });
        });
        setNotice({ tone: "green", text: "" });
        return res;
      } catch (e) {
        if (e.kind === "auth") setStatus("needs-signin");
        else setNotice({ tone: "amber", text: e.message });
        return null;
      } finally {
        setPending(null);
      }
    },
    [boardName],
  );

  const addWork = useCallback(
    async (title, why) => {
      try {
        const res = await ccAddWork(title, why);
        setItems((prev) => [
          ...(prev || []),
          {
            slug: res.slug,
            kind: "idea",
            state: "INTAKE",
            sort: 900,
            updatedAt: new Date().toISOString(),
            payload: { title, why: why || null, jeff: { added: { at: new Date().toISOString() } } },
          },
        ]);
        return res;
      } catch (e) {
        if (e.kind === "auth") setStatus("needs-signin");
        else setNotice({ tone: "amber", text: e.message });
        return null;
      }
    },
    [],
  );

  // Optional live-refresh (email boards): while the tab is visible, re-load
  // every pollMs milliseconds so newly-arrived mail appears without a manual
  // refresh. Ingest is now event-driven (IMAP IDLE watcher); this poll is only
  // the last UI leg. Invisible tabs don't poll; tab-visible still refreshes via
  // the visibilitychange handler above.
  useEffect(() => {
    if (!pollMs || pollMs <= 0) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, pollMs);
    return () => clearInterval(t);
  }, [pollMs, load]);

  return { items, meta, status, pending, notice, setNotice, act, edit, addWork, refresh: load, live: status === "live" };
}